/**
 * useManageSubscription Hook
 * Orchestrates data fetching and upgrade initiation via mediator
 */

import { useCallback, useEffect } from 'react';
import { logger } from '@/application/core';
import {
  GetSubscriptionInfoQuery,
  InitiateUpgradeCommand,
  mediator,
  type SubscriptionInfo,
  type UpgradeInitiationResult,
} from '@/application';
import { useManageSubscriptionStore } from './ManageSubscriptionPage.store';

/**
 * Hook return type
 */
interface UseManageSubscriptionResult {
  data: SubscriptionInfo | null;
  loading: boolean;
  error: Error | null;
  upgrading: boolean;
  upgradeError: Error | null;
  refetch: () => Promise<void>;
  initiateUpgrade: (targetPlan: string) => Promise<void>;
}

/**
 * Hook to fetch subscription info and initiate upgrades
 * Uses mediator.send() for CQRS pattern
 */
export function useManageSubscription(): UseManageSubscriptionResult {
  const data = useManageSubscriptionStore((s) => s.data);
  const loading = useManageSubscriptionStore((s) => s.loading);
  const error = useManageSubscriptionStore((s) => s.error);
  const upgrading = useManageSubscriptionStore((s) => s.upgrading);
  const upgradeError = useManageSubscriptionStore((s) => s.upgradeError);
  const setData = useManageSubscriptionStore((s) => s.setData);
  const setLoading = useManageSubscriptionStore((s) => s.setLoading);
  const setError = useManageSubscriptionStore((s) => s.setError);
  const setUpgrading = useManageSubscriptionStore((s) => s.setUpgrading);
  const setUpgradeError = useManageSubscriptionStore((s) => s.setUpgradeError);

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const result = await mediator.send<SubscriptionInfo>(
        new GetSubscriptionInfoQuery()
      );
      setData(result);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch subscription info');
      setError(error);
      logger.error('useManageSubscription', 'Error:', err);
    }
  }, [setData, setLoading, setError]);

  const initiateUpgrade = useCallback(
    async (targetPlan: string) => {
      setUpgradeError(null);
      setUpgrading(true);

      try {
        const result = await mediator.send<UpgradeInitiationResult>(
          new InitiateUpgradeCommand(targetPlan)
        );

        // Redirect to Stripe checkout
        if (result.stripeUrl) {
          window.location.href = result.stripeUrl;
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to initiate upgrade');
        setUpgradeError(error);
        logger.error('useManageSubscription', 'Upgrade Error:', err);
      }
    },
    [setUpgrading, setUpgradeError]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    upgrading,
    upgradeError,
    refetch: fetchData,
    initiateUpgrade,
  };
}
